import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import LaunchBanner from "../components/LaunchBanner";
import Header          from "../components/Header";
import CopyrightFooter from "../components/CopyrightFooter";
import api             from "../api";
import { track }       from "../utils/analytics";     

import CheckMark       from "../assets/check-mark.png";

import "../styles/PricingPage.css";
import "../styles/buttons.css";

const PricingPageBusinesses = () => {
  const navigate = useNavigate();

  /* ───────── body class cleanup ───────── */
  useEffect(() => {
    document.body.classList.add("pricing-page-body");
    return () => document.body.classList.remove("pricing-page-body");
  }, []);

  /* ───────── GA4 page view ───────── */
  useEffect(() => {
    track("page_view", {
      page_location: window.location.href,
      page_title   : "Pricing – Businesses",
    });
  }, []);

  /* ───────── state ───────── */
  const [email,  setEmail]  = useState("");
  const [banner, setBanner] = useState("");

  /* ───────── helpers ───────── */

  /* trial email capture */
  const handleRequestTrial = async () => {
    track("request_trial_click", { account_type: "business" });

    if (!email || !email.includes("@")) {
      setBanner("Please enter a valid email.");
      track("start_trial_error", {
        account_type: "business",
        message     : "invalid_email",
      });
      return;
    }

    try {
      await api.post("/start_trial/", {
        email,
        account_type: "business",
      });

      track("start_trial_success", { account_type: "business" });
      window.gtag?.("set", "user_properties", { has_trial: true });

      setBanner("✅ Check your inbox for the magic link!");
      setEmail("");
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "api_error";

      track("start_trial_error", { account_type: "business", message });
      setBanner(`❌ ${message}`);
    }
  };

  /* navigate with tracking */
  const goToSignup = (plan) => {
    track("select_plan_click", { plan_id: plan, billing: "self" });
    window.gtag?.("set", "user_properties", { chosen_plan: plan });

    navigate("/signup", { state: { accountType: "business", plan } });
  };

  /* ───────── render ───────── */
  return (
    <div className="pricing-page">
      <LaunchBanner goToSignup={goToSignup} />
      <Header />

      {/* ─── HERO ───────────────────────────────────────────────── */}
      <div className="content-above-cards" id="business-plans">
        <header className="pricing-header">
          <h1 className="pricing-tagline">Plans &amp; Pricing</h1>
          <p className="pricing-subheading">
            Get Started for Free and Choose the Plan that Fits Your Needs.
          </p>
          <p className="pricing-description">
            Pick the Perfect Plan for Your Event. Upgrade or Adjust as Your
            Needs Evolve.
          </p>

          {/* trial email capture */}
          <div className="email-trial">
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRequestTrial}
            >
              Request a trial
            </button>
          </div>
          {banner && <p className="banner-message">{banner}</p>}

          <p className="no-credit-info">
            Try BestDay2Marry for free. By entering your email, you agree to
            receive marketing emails from BestDay2Marry.
          </p>
        </header>
      </div>

      {/* ─── CARDS ──────────────────────────────────────────────── */}
      <div className="pricing-cards-container">
        <header className="plans-header">
          <h2>Business Plans</h2>
        </header>

        <div className="pricing-cards">

          {/* -------- Trial -------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Trial</h3>
            <p className="price">Free</p>
            <div className="card-divider" />

            <ul className="features-list">
              {[
                "1 week access",
                "1 location",
                "1 date",
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <div className="card-divider" />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => goToSignup("trial")}
            >
              Try for free
            </button>
          </div>

          {/* -------- Standard -------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Standard</h3>
            <p className="price">
              $500 <span>/ month</span>
            </p>
            <div className="card-divider" />

            <ul className="features-list">
              {[
                "Monthly access",
                "1 location",
                "1 date",
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <div className="card-divider" />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => goToSignup("standard")}
            >
              Get started
            </button>
          </div>

          {/* -------- Premium -------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Premium</h3>
            <p className="price">
              $700 <span>/ month</span>
            </p>
            <div className="card-divider" />

            <ul className="features-list">
              {[
                "Monthly access",
                "1 location",
                "1 date",
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
                "Extreme Weather: Fires, Flooding, Storms",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <div className="card-divider" />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => goToSignup("premium")}
            >
              Get started
            </button>
          </div>

          {/* -------- Custom -------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Custom</h3>
            <p className="price">
              Price varies <span>/ month</span>
            </p>
            <div className="card-divider" />

            <ul className="features-list">
              {[
                "Monthly access",
                "Choose locations",
                "Choose number of dates",
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
                "Extreme Weather: Fires, Flooding, Storms",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <div className="card-divider" />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => track("call_us_click", { location: "pricing_business" })}
            >
              Call Us
            </button>
          </div>
        </div>
      </div>

      <CopyrightFooter />
    </div>
  );
};

export default PricingPageBusinesses;